import { chain } from 'ramda';

import {
  ISumData,
  ISumCategory,
} from '../Queries/EkonomiskAnalysSettingsQuery/EkonomiskAnalysSettingsQuery';
import {
  TEDInputAvenyEKAnalysSum,
  TEDInputAvenyEKAnalysColumn,
  TEDInputAvenyEKTimeInterval,
  TEDInputAvenyEKPartialTimeInterval,
  TEDInputAvenyEKAnalys,
  TEDEKRapportDimensionTypEnum,
} from '../../../../../../globalQueryTypes';
import { ekanalys_ekanalys_columns } from '../Queries/EkonomiskAnalysSettingsQuery/_QueryTypes/ekanalys';
import { ISuggestion } from '../../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import {
  ISelectedAnalysAnalys,
  ISelectedAnalysCodePrivilege,
} from '../EkonomiskAnalysPage';
import {
  IEkonomiskAnalysFilledDates,
  IEkonomiskAnalysDates,
} from '../Components/AnalysSettingsHandler/AnalysSettingsHandler';
import { ILocalAnalys } from '../Components/EkonomiskAnalysMain/EkonomiskAnalysMain';

/**
 * Konverterar ett summeringsbegrepp som vi lagrar det i frontend
 * till att följa det format som backenden vill ha som input
 */
export const iSumDataToInputSum = (
  sumData: ISumData
): TEDInputAvenyEKAnalysSum => {
  // Vår datatyp har attributename, som backended inte vill ha, så får graphql fel om vi skickar med den
  const { attributename, ...inputSum } = sumData;
  return inputSum;
};

/**
 * Returnerar alla summeringsbegrepp för kontodelar. Currently kept to kontodel_1
 */
export const kontodelarSumsFromCategories = (
  categories: ISumCategory[] | undefined
): ISumData[] => {
  if (!categories) {
    return [];
  }

  const kontodel = chain(x => x.subCategories, categories).filter(
    x => x.code === 'KONTODEL_1'
  );
  return chain(x => x.sums, kontodel);
};

export const colToInputCol = (
  column: ekanalys_ekanalys_columns
): TEDInputAvenyEKAnalysColumn => ({
  LOPNR: column.LOPNR,
  ORDNINGS_NR: column.ORDNINGS_NR,
  FALTKOD: column.FALTKOD,
  INTERN: column.INTERN,
  RESULTAT_TYP: column.RESULTAT_TYP,
  FALT_TYP: column.FALT_TYP,
  INTERVALL_TYP: column.INTERVALL_TYP,
  BAS: column.BAS,
  ANTAL_DECIMALER: column.ANTAL_DECIMALER,
  VAND_TECKEN: column.VAND_TECKEN,
  BENAMNING: column.BENAMNING,
  BENAMNING2: column.BENAMNING2,
  CODE_CAPTION: column.CODE_CAPTION,
  QUERY_TYP: null, // TODO: What is this?
  FROM_AR: column.FROM_AR,
  FROM_AR_RELATIV: column.FROM_AR_RELATIV,
  TOM_AR: column.TOM_AR,
  TOM_AR_RELATIV: column.TOM_AR_RELATIV,
  FROM_PER: column.FROM_PER,
  FROM_PER_RELATIV: column.FROM_PER_RELATIV,
  TOM_PER: column.TOM_PER,
  TOM_PER_RELATIV: column.TOM_PER_RELATIV,
  ENHET_ID: column.ENHET_ID,
  BUDGETTYP: column.BUDGETTYP,
  BUDGETBENAMNING: column.BUDGETBENAMNING,
  RULLANDE: column.RULLANDE,
  HIDE_COLUMN: column.HIDE_COLUMN,
  FORCE_CALCULATE: column.FORCE_CALCULATE,
  SPECIFIKATION: column.SPECIFIKATION,
});

export const localAnalysToTedEkAnalys = (
  analys: ILocalAnalys,
  id?: string,
  forceHelarsjamforelse: boolean = true
): TEDInputAvenyEKAnalys => {
  return {
    columns: analys.columns.map(x => colToInputCol(x.data)),
    endast_bokfort: !analys.other.includeEjBokfordaJournaler,
    force_helarsjamforelse: forceHelarsjamforelse,
    kategori: analys.category,
    name: analys.name,
    rubrik: analys.title,
    standard_belopp_antal_decimaler: analys.other.decimals,
    standard_belopp_bas: analys.other.denomination.toString(),
    sums: analys.sums.map(iSumDataToInputSum),
    typ: analys.type as TEDEKRapportDimensionTypEnum,
    anvands_for: analys.anvandsFor,
    egen_analys: true,
    id: id,
  };
};

export const suggestionToAnalys = (
  suggestion: ISuggestion
): ISelectedAnalysAnalys => {
  const grundurval = suggestion.data
    ? suggestion.data.baseSelection
    : undefined;

  return {
    name: suggestion.name,
    id: suggestion.value,
    idkod: suggestion.data.idkod,
    grundurval: grundurval,
    utdelningLopnr: suggestion.data.utdelatLopnr,
    defaultFromYear: suggestion.data.defaultFromYear,
    defaultFromPeriod: suggestion.data.defaultFromPeriod,
    category: suggestion.data.kategori,
    categoryID: suggestion.data.kategori_ID,
    detaljanalys: suggestion.data.detaljanalys,
    ai_prompt: suggestion.data.ai_prompt,
  };
};

export const suggestionToCodePrivilege = (
  suggestion: ISuggestion
): ISelectedAnalysCodePrivilege => ({
  name: suggestion.name,
  id: suggestion.value,
});

export const datesToTimeInterval = (
  dates: IEkonomiskAnalysFilledDates
): TEDInputAvenyEKTimeInterval => {
  const fromDay = dates.from.month !== 13 ? dates.from.day : undefined;
  return {
    fromYear: dates.from.year,
    fromMonth: dates.from.month,
    fromDay: fromDay,
    toYear: dates.to.year ? dates.to.year : dates.from.year,
    toMonth: dates.to.month ? dates.to.month : dates.from.month,
    toDay:
      dates.to.month === 13 ? undefined : dates.to.day ? dates.to.day : fromDay,
  };
};

export const datesToPartialTimeInterval = (
  dates: IEkonomiskAnalysDates,
  isInterval: boolean
): TEDInputAvenyEKPartialTimeInterval => {
  if (dates.from.error || (isInterval && dates.to.error)) {
    return {
      fromYear: null,
      fromMonth: null,
      fromDay: null,
      toYear: null,
      toMonth: null,
      toDay: null,
    };
  }
  const fromDay = dates.from.month !== 13 ? dates.from.day : undefined;
  return {
    fromYear: dates.from.year,
    fromMonth: dates.from.month,
    fromDay: fromDay,
    toYear: dates.to.year ? dates.to.year : dates.from.year,
    toMonth: dates.to.month ? dates.to.month : dates.from.month,
    toDay:
      dates.to.month === 13 ? undefined : dates.to.day ? dates.to.day : fromDay,
  };
};

export const unfilledEkonomiskDateToFilled = (
  date: IEkonomiskAnalysDates
): IEkonomiskAnalysFilledDates | null => {
  const { from, to } = date;
  if (from.year && from.month) {
    return {
      from: { year: from.year, month: from.month, day: from.day },
      to: {
        year: to.year ? to.year : from.year,
        month: to.month ? to.month : from.month,
        day: to.day ? to.day : from.day,
      },
    };
  }

  return null;
};
